/**
 * The ch06 feature manifest — the post-season learning loop.
 *
 * Six routes, and the order in navigation is deliberately **not** the order of reqs/06's
 * numbered capabilities. It is the order a planner reads them in:
 *
 * | Route | reqs/06 | The question it answers |
 * |---|---|---|
 * | `review` | 6.1 | How did the season actually go, by option, cluster and channel? |
 * | `lost-sales` | 6.4 | How much demand did we never see, and how much do we trust that number? |
 * | `accuracy` | 6.3 | Was the forecast any good, and did it beat seasonal-naive? |
 * | `attributes` | 6.2 | Which colours, materials and price bands performed? |
 * | `governance` | 6.5 | What is being refreshed, what churned, and what was promoted? |
 * | `suppliers` | 6.6 | Who delivered on time, in full, and to spec? |
 * | `promotions` | issue 314 | Which promotion windows did the tills show, and does Bata's weekly label agree? |
 * | `promotions/window` | issue 314 | What did one window do, channel by channel, against its neighbouring days? |
 * | `promotions/mechanics` | issue 314 | What does each mechanic do across its windows, and how sure is that? |
 *
 * `lost-sales` sits second rather than fourth because reqs/06 §1's sequencing rule is a
 * property of the numbers on every other screen: everything on `review`, `accuracy` and
 * `attributes` is computed on the demand `lost-sales` reconstructs. A planner who has not
 * seen the recovery census cannot judge any of the rest, so it is one click from the
 * review rather than buried behind it.
 *
 * Every route declares `exports` and `drill`, because the manifest types make both
 * required on a data route — G2 tests *d* and *f* are type errors here rather than census
 * failures later.
 */

import { defineFeature } from '../manifest';
import { POSTSEASON_GUIDES } from './guides';

import {
  accuracyExport,
  attributeExport,
  governanceExport,
  lostSalesCensusExport,
  lostSalesExport,
  lostSalesPotentialExport,
  seasonReviewExport,
  supplierExport,
} from './exports';
import {
  promotionMechanicsExport, promotionWindowArticlesExport, promotionWindowExport, promotionWindowsExport, promotionWindowStoreExport,
  promotionWindowStoresExport,
} from './promotions/exports';

export default defineFeature({
  slug: 'postseason',
  titleKey: 'postseason.title',
  navLabelKey: 'postseason.navLabel',
  icon: 'chart-line',  // brand guide p.14
  railGroup: 'setup',  // bata-ui#132 — configuration and analysis
  localeNamespace: 'postseason',
  // Every route's guide and guided tour (ADR 9044) — one entry per route below, or the census fails.
  guides: POSTSEASON_GUIDES,
  navigation: [
    { labelKey: 'postseason.nav.review', to: '/postseason/review', order: 60 },
    // One page, one function (client rule 3, DEC-88, ADR 8001): the census, the potential by door
    // and the explorer of the named panel are three pages, drawn as Lost sales' sub-entries.
    // Sibling prefixes, because `lost-sales/:optionKey` is the declared drill.
    {
      labelKey: 'postseason.nav.lostSales',
      to: '/postseason/lost-sales',
      order: 61,
      children: [
        { labelKey: 'postseason.nav.lostSalesCensus', to: '/postseason/lost-sales', order: 1 },
        { labelKey: 'postseason.nav.lostSalesPotential', to: '/postseason/lost-sales-potential', order: 2 },
        { labelKey: 'postseason.nav.lostSalesPanel', to: '/postseason/lost-sales-panel', order: 3 },
      ],
    },
    { labelKey: 'postseason.nav.accuracy', to: '/postseason/accuracy', order: 62 },
    { labelKey: 'postseason.nav.attributes', to: '/postseason/attributes', order: 63 },
    { labelKey: 'postseason.nav.governance', to: '/postseason/governance', order: 64 },
    { labelKey: 'postseason.nav.suppliers', to: '/postseason/suppliers', order: 65 },
    // Issue 314 (ADR 8004): one page per function (client rule 3) — the windows of a period, and
    // the mechanics across their windows. A window's own page is the timeline's drill, not an entry.
    {
      labelKey: 'postseason.nav.promotions',
      to: '/postseason/promotions',
      order: 66,
      children: [
        { labelKey: 'postseason.nav.promotionWindows', to: '/postseason/promotions', order: 1 },
        { labelKey: 'postseason.nav.promotionMechanics', to: '/postseason/promotions/mechanics', order: 2 },
      ],
    },
  ],
  routes: [
    {
      path: 'review',
      titleKey: 'postseason.review.title',
      urlParams: ['season'],
      component: () => import('./screens/SeasonReviewScreen'),
      surfaces: ['table', 'chart', 'tile'],
      tableViewReachable: true,
      exports: {
        id: 'postseason.review.table',
        formats: ['csv', 'xlsx'],
        resolve: seasonReviewExport,
      },
      // `/postseason/review/:optionKey` was declared here and never mounted (the #203 audit), and
      // it cannot be: `GET /postseason/review` takes no option — only `limit` and `cursor` — and
      // no other read serves one option's review, so a detail page would be the whole mart
      // re-paged and filtered in the browser. A row is also option × cluster × channel, so an
      // option key would not name one row. Terminal until the platform publishes that read.
      drill: {
        terminal: true,
        reason: 'No published read serves one option’s season review: GET /postseason/review takes no option parameter, and a row is option × cluster × channel, so there is no level below the row this page can honestly open.',
      },
    },
    {
      // CENSUS — the season's latest recovery run: store-weeks by state, the control totals, the
      // model evidence. The same address as ever, so every link already sent lands here.
      path: 'lost-sales',
      titleKey: 'postseason.lostSales.title',
      urlParams: ['season'],
      component: () => import('./screens/LostSalesScreen'),
      surfaces: ['table', 'chart', 'tile'],
      tableViewReachable: true,
      exports: {
        id: 'postseason.lostSales.census',
        formats: ['csv', 'xlsx'],
        resolve: lostSalesCensusExport,
      },
      drill: {
        to: '/postseason/lost-sales-panel',
        grain: 'store',
        alsoTo: ['/postseason/review'],
      },
    },
    {
      // POTENTIAL — assortment and late-ranging potential by door, never totalled.
      path: 'lost-sales-potential',
      titleKey: 'postseason.lostSalesPotential.title',
      urlParams: ['article', 'season', 'store', 'week'],
      component: () => import('./screens/LostSalesPotentialScreen'),
      // Template S (C6): the ten doors with the most assortment potential are a chart.
      surfaces: ['table', 'chart', 'tile'],
      tableViewReachable: true,
      exports: {
        id: 'postseason.lostSales.potential',
        formats: ['csv', 'xlsx'],
        resolve: lostSalesPotentialExport,
      },
      drill: { to: '/postseason/lost-sales-panel', grain: 'store' },
    },
    {
      // EXPLORER — the season's recovery coverage (one row per door, from its run in force; its
      // CSV is the coverage table's own, ADR 8002), then the recovered-demand panel for the cut
      // the planner opens or names (doors, weeks, articles): its basis, its door × week map and
      // its cells. The route's export is the cut's cells, each with its run.
      path: 'lost-sales-panel',
      titleKey: 'postseason.lostSalesPanel.title',
      urlParams: ['article', 'season', 'store', 'week'],
      component: () => import('./screens/LostSalesPanelScreen'),
      // Template S (C6): the cut's door × week heatmap is a chart.
      surfaces: ['table', 'chart', 'tile'],
      tableViewReachable: true,
      exports: {
        id: 'postseason.lostSales.table',
        formats: ['csv', 'xlsx'],
        resolve: lostSalesExport,
      },
      drill: {
        to: '/postseason/lost-sales/:optionKey',
        grain: 'size',
        alsoTo: ['/postseason/review'],
      },
    },
    {
      path: 'accuracy',
      titleKey: 'postseason.accuracy.title',
      urlParams: [],
      component: () => import('./screens/AccuracyScreen'),
      surfaces: ['table', 'chart'],
      tableViewReachable: true,
      exports: {
        id: 'postseason.accuracy.table',
        formats: ['csv', 'xlsx'],
        resolve: accuracyExport,
      },
      drill: {
        to: '/postseason/accuracy/:categoryKey',
        grain: 'category',
        alsoTo: ['/postseason/governance'],
      },
    },
    {
      path: 'attributes',
      titleKey: 'postseason.attributes.title',
      urlParams: ['season'],
      component: () => import('./screens/AttributeLearningScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'postseason.attributes.table',
        formats: ['csv', 'xlsx'],
        resolve: attributeExport,
      },
      drill: {
        to: '/postseason/attributes/:attributeName',
        grain: 'category',
        alsoTo: ['/postseason/review'],
      },
    },
    {
      path: 'governance',
      titleKey: 'postseason.governance.title',
      urlParams: [],
      component: () => import('./screens/RefreshGovernanceScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'postseason.governance.table',
        formats: ['csv', 'xlsx'],
        resolve: governanceExport,
      },
      drill: {
        to: '/postseason/governance/:schemeId',
        grain: 'cluster',
        alsoTo: ['/postseason/accuracy'],
      },
    },
    {
      path: 'suppliers',
      titleKey: 'postseason.suppliers.title',
      urlParams: ['season'],
      component: () => import('./screens/SupplierScorecardScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'postseason.suppliers.table',
        formats: ['csv', 'xlsx'],
        resolve: supplierExport,
      },
      // A supplier's PO-line detail is the next level down and is chapter 02's buy
      // screen, which does not exist until Wave 4. Declared as a target rather than as a
      // terminal, because it is a route that will exist.
      drill: { to: '/postseason/suppliers/:supplierKey', grain: 'article' },
    },
    {
      // Issue 314 (ADR 8004): the promotion windows the tills show in a period, with Bata's weekly
      // label beside each. `?market=&from=&to=`, none defaulted. Read not pinned yet («In arrivo»).
      path: 'promotions',
      titleKey: 'postseason.promotions.windows.title',
      urlParams: ['from', 'market', 'to', 'window'],
      component: () => import('./promotions/WindowsScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: 'postseason.promotions.windows', formats: ['csv', 'xlsx'], resolve: promotionWindowsExport },
      // A window is a run of days: its page states it day to day and channel by channel.
      drill: { to: '/postseason/promotions/window', grain: 'day', alsoTo: ['/postseason/promotions/mechanics'] },
    },
    {
      // One window's channel impacts, opened from the timeline (`?window=`, with its scope).
      path: 'promotions/window',
      titleKey: 'postseason.promotions.window.title',
      urlParams: ['from', 'market', 'to', 'window'],
      component: () => import('./promotions/WindowScreen'),
      surfaces: ['table'],
      exports: { id: 'postseason.promotions.window', formats: ['csv', 'xlsx'], resolve: promotionWindowExport },
      // Issue 314 and owner demo 2026-10-09 request 6 (`docs/adr/8005`, bata-platform ADR 8016): a
      // window's channel opens its stores — the reference perimeter of the comparison — and, across,
      // the articles the window sold.
      drill: { to: '/postseason/promotions/window/stores', grain: 'store', alsoTo: ['/postseason/promotions/window/articles'] },
    },
    {
      // A window's stores: every door that sold on a window day or a neighbouring day, whether it
      // entered the comparison and why not. `?market=&window=`, neither defaulted.
      path: 'promotions/window/stores',
      titleKey: 'postseason.promotions.stores.title',
      // `store` is WRITTEN here, on each store's link (the census counts a write, ADR 9054).
      urlParams: ['from', 'market', 'store', 'to', 'window'],
      component: () => import('./promotions/WindowStoresScreen'),
      surfaces: ['table'],
      exports: { id: 'postseason.promotions.window.stores', formats: ['csv', 'xlsx'], resolve: promotionWindowStoresExport },
      // Issue 314's second level (`docs/adr/8006`, bata-platform ADR 8017): a store opens its
      // sub-categories in the window.
      drill: { to: '/postseason/promotions/window/store', grain: 'subcategory' },
    },
    {
      // One store's sub-categories in a window: units and net in the window and around it, the
      // share of the store's units, the change against the days around it or why none is stated.
      // `?market=&window=&store=`, none defaulted.
      path: 'promotions/window/store',
      titleKey: 'postseason.promotions.store.title',
      urlParams: ['from', 'market', 'store', 'to', 'window'],
      component: () => import('./promotions/WindowStoreScreen'),
      surfaces: ['table'],
      exports: { id: 'postseason.promotions.window.store', formats: ['csv', 'xlsx'], resolve: promotionWindowStoreExport },
      drill: {
        terminal: true,
        reason: 'A sub-category of one store in one window is the finest grain GET /postseason/promotions/windows/{window_id}/doors/{store_key}/subcategories serves (bata-data ADR 8013 lands window × channel × door × sub-category); the window’s articles, across the doors, are on /postseason/promotions/window/articles.',
      },
    },
    {
      // A window's articles: units, units on promotion lines, share of the channel's units, booked net.
      path: 'promotions/window/articles',
      titleKey: 'postseason.promotions.articles.title',
      urlParams: ['from', 'market', 'to', 'window'],
      component: () => import('./promotions/WindowArticlesScreen'),
      surfaces: ['table'],
      exports: { id: 'postseason.promotions.window.articles', formats: ['csv', 'xlsx'], resolve: promotionWindowArticlesExport },
      drill: {
        terminal: true,
        reason: 'An article over a window’s days is the article-day panel’s finest grain served by GET /postseason/promotions/windows/{window_id}/articles (bata-data ADR 8010 lands door × day × article, read here summed over the doors); there is no level below an article.',
      },
    },
    {
      // Per mechanic × channel across its windows, every figure with its interval, never ranked.
      path: 'promotions/mechanics',
      titleKey: 'postseason.promotions.mechanics.title',
      urlParams: ['from', 'market', 'to', 'window'],
      component: () => import('./promotions/MechanicsScreen'),
      surfaces: ['table', 'chart'],
      tableViewReachable: true,
      exports: { id: 'postseason.promotions.mechanics', formats: ['csv', 'xlsx'], resolve: promotionMechanicsExport },
      drill: {
        terminal: true,
        reason: 'GET /postseason/promotions/mechanics publishes a mechanic group × channel summary and no list of the windows it was computed over, so a row has no published level below it; the windows themselves are on the timeline, by period.',
      },
    },
  ],
});
